// src/components/setting/RoleManager.jsx
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Plus, Trash2, Pencil, Shield, Check, RefreshCw, Lock,
  LayoutDashboard, Users, Calendar, CreditCard, BarChart2,
  FileCheck, Settings, UserPlus, Gift, Sun, Download, FileText
} from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import apiClient from '../../services/apiClient';

// ── All permission groups with granular per-action keys ───────────────────────
const PERMISSION_GROUPS = [
  {
    label: 'Dashboard',
    icon: LayoutDashboard,
    color: 'text-slate-600', bg: 'bg-slate-100', border: 'border-slate-200',
    description: 'Access to the main dashboard',
    permissions: [
      { key: 'view_dashboard', label: 'View Dashboard', desc: 'See employee count, payroll stats, pending tasks' },
    ],
  },
  {
    label: 'Employees',
    icon: Users,
    color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200',
    description: 'Employee records and profiles',
    permissions: [
      { key: 'view_employees',   label: 'View',    desc: 'List employees, open profiles, see salary structure' },
      { key: 'create_employee',  label: 'Create',  desc: 'Add new employees to the system'                    },
      { key: 'edit_employee',    label: 'Edit',    desc: 'Update name, department, salary, compliance flags'  },
      { key: 'archive_employee', label: 'Archive', desc: 'Soft-delete and restore employee records'           },
    ],
  },
  {
    label: 'Attendance',
    icon: Calendar,
    color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200',
    description: 'Daily attendance marking and records',
    permissions: [
      { key: 'view_attendance', label: 'View',            desc: 'See attendance records and monthly summaries'          },
      { key: 'mark_attendance', label: 'Mark Attendance', desc: 'Save Present/Absent/Leave/OT for employees per day'    },
    ],
  },
  {
    label: 'Leave',
    icon: FileText,
    color: 'text-violet-700', bg: 'bg-violet-50', border: 'border-violet-200',
    description: 'Leave balances (PL, CL, SL, LOP)',
    permissions: [
      { key: 'view_leave',   label: 'View Leave',        desc: 'See leave balances for all employees by year'    },
      { key: 'adjust_leave', label: 'Adjust Balance',    desc: 'Manually add or deduct leave days with a note'   },
    ],
  },
  {
    label: 'Holidays',
    icon: Sun,
    color: 'text-rose-700', bg: 'bg-rose-50', border: 'border-rose-200',
    description: 'Holiday master list management',
    permissions: [
      { key: 'view_holidays',   label: 'View',    desc: 'See the holiday list for the current year'                         },
      { key: 'manage_holidays', label: 'Manage',  desc: 'Add / remove holidays, seed national + state holidays'             },
    ],
  },
  {
    label: 'Payroll',
    icon: CreditCard,
    color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200',
    description: 'Payroll calculation, approval and payment',
    permissions: [
      { key: 'view_payroll',       label: 'View',          desc: 'See payroll runs, salary breakdowns, LOP details'    },
      { key: 'calculate_payroll',  label: 'Calculate',     desc: 'Run the payroll preview/calculate step'              },
      { key: 'create_payroll_run', label: 'Create Run',    desc: 'Finalise and save a payroll draft'                   },
      { key: 'approve_payroll',    label: 'Approve',       desc: 'Approve a payroll run for payment'                   },
      { key: 'mark_payroll_paid',  label: 'Mark as Paid',  desc: 'Mark run as paid and send WhatsApp notifications'    },
    ],
  },
  {
    label: 'Payslips & Exports',
    icon: Download,
    color: 'text-indigo-700', bg: 'bg-indigo-50', border: 'border-indigo-200',
    description: 'Download payroll data in various formats',
    permissions: [
      { key: 'view_payslips',       label: 'View Payslips',    desc: 'Access payslip history and individual payslip PDFs'  },
      { key: 'export_payroll_csv',  label: 'Export CSV',       desc: 'Download payroll run as CSV'                         },
      { key: 'export_payroll_excel',label: 'Export Excel',     desc: 'Download full payroll Excel workbook'                },
      { key: 'export_bank_file',    label: 'Bank File',        desc: 'Download bank transfer file for payment processing'  },
    ],
  },
  {
    label: 'Salary Advances',
    icon: Gift,
    color: 'text-orange-700', bg: 'bg-orange-50', border: 'border-orange-200',
    description: 'Employee salary advance tracking (Pro)',
    permissions: [
      { key: 'view_advances',  label: 'View',   desc: 'See all salary advances and recovery schedule'  },
      { key: 'create_advance', label: 'Create', desc: 'Record a new salary advance for an employee'    },
      { key: 'waive_advance',  label: 'Waive',  desc: 'Write off an outstanding salary advance'        },
    ],
  },
  {
    label: 'Reports',
    icon: BarChart2,
    color: 'text-teal-700', bg: 'bg-teal-50', border: 'border-teal-200',
    description: 'Analytics, YTD reports and cost breakdowns',
    permissions: [
      { key: 'view_reports', label: 'View Reports', desc: 'Payroll trends, YTD per employee, department cost breakdown' },
    ],
  },
  {
    label: 'Compliance',
    icon: FileCheck,
    color: 'text-purple-700', bg: 'bg-purple-50', border: 'border-purple-200',
    description: 'PF, ESI and Professional Tax config',
    permissions: [
      { key: 'view_compliance',   label: 'View',    desc: 'See PF/ESI/PT configuration and contribution summaries' },
      { key: 'manage_compliance', label: 'Manage',  desc: 'Update PF/ESI/PT settings and generate challans'        },
    ],
  },
  {
    label: 'Settings',
    icon: Settings,
    color: 'text-slate-700', bg: 'bg-slate-100', border: 'border-slate-200',
    description: 'Business profile and payroll configuration',
    permissions: [
      { key: 'view_settings',           label: 'View Settings',          desc: 'See business profile and payroll config'             },
      { key: 'edit_business_profile',   label: 'Edit Business Profile',  desc: 'Update name, state, GSTIN, phone, owner name'        },
      { key: 'edit_payroll_config',     label: 'Edit Payroll Config',    desc: 'Change working days, overtime rate, Sunday toggle'   },
      { key: 'manage_salary_components',label: 'Salary Components',      desc: 'Add/edit/remove custom salary components'            },
    ],
  },
  {
    label: 'Team & Roles',
    icon: UserPlus,
    color: 'text-cyan-700', bg: 'bg-cyan-50', border: 'border-cyan-200',
    description: 'Team member invitations and role management',
    permissions: [
      { key: 'view_team',    label: 'View Team',      desc: 'See team members list and pending invites'          },
      { key: 'invite_member',label: 'Invite Members', desc: 'Send invites via link or WhatsApp'                  },
      { key: 'remove_member',label: 'Remove Members', desc: 'Revoke access for existing team members'            },
      { key: 'manage_roles', label: 'Manage Roles',   desc: 'Create, edit and delete custom permission roles'    },
    ],
  },
];

const ALL_KEYS = [...new Set(PERMISSION_GROUPS.flatMap(g => g.permissions.map(p => p.key)))];

const api = {
  list:   () => apiClient.get('/api/teams/roles').then(r => r.data),
  create: (d) => apiClient.post('/api/roles', d).then(r => r.data),
  update: (id, d) => apiClient.put(`/api/roles/${id}`, d).then(r => r.data),
  delete: (id) => apiClient.delete(`/api/roles/${id}`).then(r => r.data),
};

// ── Permission toggle ─────────────────────────────────────────────────────────
function PermToggle({ pkey, label, desc, checked, onChange, disabled }) {
  return (
    <button type="button" disabled={disabled} onClick={() => !disabled && onChange(pkey)}
      className={[
        'flex items-start gap-2.5 px-3 py-2.5 rounded-xl border text-left transition-all w-full',
        disabled ? 'opacity-40 cursor-not-allowed bg-slate-50 border-slate-200'
          : checked ? 'bg-[#2E6DA4] border-[#2E6DA4] shadow-sm'
          : 'bg-white border-slate-200 hover:border-[#2E6DA4]/40 hover:bg-blue-50/20',
      ].join(' ')}>
      <span className={`w-4 h-4 rounded flex-shrink-0 mt-0.5 border flex items-center justify-center ${
        checked ? 'bg-white border-white' : 'border-slate-300 bg-white'
      }`}>
        {checked && <Check className="w-2.5 h-2.5 text-[#2E6DA4]" />}
      </span>
      <div className="min-w-0 flex-1">
        <div className={`text-sm font-semibold leading-tight ${checked ? 'text-white' : 'text-slate-800'}`}>{label}</div>
        <div className={`text-[11px] mt-0.5 leading-snug ${checked ? 'text-blue-100' : 'text-slate-400'}`}>{desc}</div>
      </div>
    </button>
  );
}

// ── Role modal ────────────────────────────────────────────────────────────────
function RoleModal({ open, onClose, existing }) {
  const qc = useQueryClient();
  const isEdit = !!existing;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [permissions, setPermissions] = useState([]);

  React.useEffect(() => {
    if (open) {
      setName(existing?.name || '');
      setDescription(existing?.description || '');
      setPermissions(existing?.permissions || []);
    }
  }, [existing, open]);

  const toggle = (k) => setPermissions(p => p.includes(k) ? p.filter(x => x !== k) : [...p, k]);

  const toggleGroup = (group) => {
    const keys = [...new Set(group.permissions.map(p => p.key))];
    const allOn = keys.every(k => permissions.includes(k));
    setPermissions(prev => allOn ? prev.filter(p => !keys.includes(p)) : [...new Set([...prev, ...keys])]);
  };

  const selected = ALL_KEYS.filter(k => permissions.includes(k)).length;

  const saveMut = useMutation({
    mutationFn: () => isEdit
      ? api.update(existing._id, { name, description, permissions })
      : api.create({ name, description, permissions }),
    onSuccess: () => { toast.success(isEdit ? 'Role updated' : 'Role created'); useQueryClient().invalidateQueries({ queryKey: ['teamRoles'] }); onClose(); },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  const handleSave = () => {
    if (!name.trim()) { toast.error('Role name required'); return; }
    if (!permissions.length) { toast.error('Select at least one permission'); return; }
    saveMut.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
        <div className="px-6 pt-5 pb-4 border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#2E6DA4]" />
            <h2 className="text-base font-bold text-slate-900 font-heading">
              {isEdit ? `Edit: ${existing.name}` : 'Create New Role'}
            </h2>
          </div>
          <p className="text-xs text-slate-500 mt-1">Select exactly which actions this role can perform.</p>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Name + desc */}
          <div className="px-6 pt-4 pb-4 border-b border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-600">Role Name *</Label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. HR Supervisor" autoFocus={!isEdit} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-600">Description</Label>
              <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Brief description" />
            </div>
          </div>

          {/* Permissions */}
          <div className="px-6 py-5 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-slate-800">Permissions</div>
                <div className="text-xs text-slate-400">{selected} of {ALL_KEYS.length} selected</div>
              </div>
              <div className="flex gap-3 text-xs">
                <button onClick={() => setPermissions([...ALL_KEYS])} className="text-[#2E6DA4] hover:underline font-medium">Select all</button>
                <span className="text-slate-200">·</span>
                <button onClick={() => setPermissions([])} className="text-slate-400 hover:underline">Clear all</button>
              </div>
            </div>

            {PERMISSION_GROUPS.map(group => {
              const GroupIcon = group.icon;
              const keys = [...new Set(group.permissions.map(p => p.key))];
              const onCount = keys.filter(k => permissions.includes(k)).length;
              const allOn = onCount === keys.length;
              const someOn = onCount > 0 && !allOn;

              return (
                <div key={group.label} className={`rounded-xl border overflow-hidden ${group.border}`}>
                  <button type="button" onClick={() => toggleGroup(group)}
                    className={`w-full flex items-center gap-3 px-4 py-3 ${group.bg} hover:brightness-95 transition-all`}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-white/70 border ${group.border}`}>
                      <GroupIcon className={`w-4 h-4 ${group.color}`} />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="text-sm font-bold text-slate-800">{group.label}</div>
                      <div className="text-[11px] text-slate-500">{group.description}</div>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${
                      allOn ? 'bg-[#2E6DA4] text-white' :
                      someOn ? 'bg-amber-100 text-amber-700 border border-amber-200' :
                               'bg-white/80 text-slate-400 border border-slate-200'
                    }`}>{onCount}/{keys.length}</span>
                  </button>
                  <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2 bg-white">
                    {group.permissions.map(p => (
                      <PermToggle key={`${group.label}-${p.key}`} pkey={p.key} label={p.label}
                        desc={p.desc} checked={permissions.includes(p.key)} onChange={toggle} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex-shrink-0 flex items-center justify-between gap-3">
          <p className="text-xs text-slate-400">{permissions.length} permission key{permissions.length !== 1 ? 's' : ''} selected</p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSave} disabled={saveMut.isPending} className="bg-[#2E6DA4] hover:bg-[#255a88] text-white">
              {saveMut.isPending ? <><RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />Saving…</> : isEdit ? 'Save Changes' : 'Create Role'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Role card ─────────────────────────────────────────────────────────────────
function RoleCard({ role, onEdit, onDelete }) {
  const grouped = PERMISSION_GROUPS.map(g => ({
    ...g,
    active: [...new Set(g.permissions.filter(p => role.permissions?.includes(p.key)).map(p => p.label))],
  })).filter(g => g.active.length > 0);

  return (
    <Card className="p-5 border-slate-200 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-2 mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-sm font-bold text-slate-900">{role.name}</h4>
            {role.isSystem && (
              <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200">
                <Lock className="w-2.5 h-2.5" /> System
              </span>
            )}
          </div>
          {role.description && <p className="text-xs text-slate-500 mt-0.5">{role.description}</p>}
        </div>
        {!role.isSystem && (
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-[#2E6DA4] hover:bg-blue-50" onClick={() => onEdit(role)}>
              <Pencil className="w-3.5 h-3.5" />
            </Button>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-500 hover:bg-red-50" onClick={() => onDelete(role)}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-1.5">
        {grouped.map(g => {
          const GroupIcon = g.icon;
          return (
            <div key={g.label} className="flex items-start gap-2">
              <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5 ${g.bg} border ${g.border}`}>
                <GroupIcon className={`w-3 h-3 ${g.color}`} />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{g.label}: </span>
                <span className="text-[11px] text-slate-700">{g.active.join(' · ')}</span>
              </div>
            </div>
          );
        })}
        {grouped.length === 0 && <p className="text-xs text-slate-400 italic">No permissions</p>}
      </div>

      <div className="mt-3 pt-3 border-t border-slate-100 text-[10px] text-slate-400">
        {role.permissions?.length || 0} keys · {grouped.length} module{grouped.length !== 1 ? 's' : ''}
      </div>
    </Card>
  );
}

// ── Delete confirm ────────────────────────────────────────────────────────────
function DeleteConfirm({ role, onClose }) {
  const qc = useQueryClient();
  const delMut = useMutation({
    mutationFn: () => api.delete(role._id),
    onSuccess: () => { toast.success('Role deleted'); qc.invalidateQueries({ queryKey: ['teamRoles'] }); onClose(); },
    onError: (e) => toast.error(e.response?.data?.message || 'Cannot delete'),
  });
  return (
    <Dialog open={!!role} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-heading text-red-600 flex items-center gap-2">
            <Trash2 className="w-5 h-5" /> Delete Role
          </DialogTitle>
          <DialogDescription>
            Delete <strong>{role?.name}</strong>? Team members with this role will lose access immediately.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => delMut.mutate()} disabled={delMut.isPending} className="bg-red-600 hover:bg-red-700 text-white">
            {delMut.isPending ? 'Deleting…' : 'Delete Role'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function RoleManager() {
  const [modalOpen, setModalOpen] = useState(false);
  const [editRole, setEditRole] = useState(null);
  const [deleteRole, setDeleteRole] = useState(null);

  const { data, isLoading } = useQuery({ queryKey: ['teamRoles'], queryFn: api.list });
  const roles = data?.data || [];
  const systemRoles = roles.filter(r => r.isSystem);
  const customRoles = roles.filter(r => !r.isSystem);

  const openEdit = (r) => { setEditRole(r); setModalOpen(true); };
  const openNew = () => { setEditRole(null); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setEditRole(null); };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-slate-900 font-heading">Role Management</h3>
          <p className="text-xs text-slate-500 mt-0.5">Granular per-action permissions. System roles are read-only.</p>
        </div>
        <Button onClick={openNew} className="bg-[#2E6DA4] hover:bg-[#255a88] text-white h-9">
          <Plus className="w-4 h-4 mr-1.5" /> New Role
        </Button>
      </div>

      {isLoading ? (
        <div className="py-12 text-center"><RefreshCw className="w-5 h-5 animate-spin text-slate-300 mx-auto mb-2" /></div>
      ) : (
        <>
          {customRoles.length > 0 && (
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Custom Roles ({customRoles.length})</p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {customRoles.map(r => <RoleCard key={r._id} role={r} onEdit={openEdit} onDelete={setDeleteRole} />)}
              </div>
            </div>
          )}
          {customRoles.length === 0 && (
            <Card className="p-10 border-dashed border-slate-200 text-center">
              <div className="w-14 h-14 rounded-full bg-slate-50 border-2 border-dashed border-slate-200 flex items-center justify-center mx-auto mb-4">
                <Shield className="w-6 h-6 text-slate-300" />
              </div>
              <p className="text-sm font-semibold text-slate-600">No custom roles yet</p>
              <p className="text-xs text-slate-400 mt-1 max-w-xs mx-auto">Create roles like "HR Supervisor" — attendance + leave access only, no payroll.</p>
              <Button onClick={openNew} className="mt-4 bg-[#2E6DA4] hover:bg-[#255a88] text-white h-9">
                <Plus className="w-4 h-4 mr-1.5" /> Create First Role
              </Button>
            </Card>
          )}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Lock className="w-3.5 h-3.5 text-slate-400" />
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400">System Roles ({systemRoles.length})</p>
              <span className="text-[10px] text-slate-300">— built-in, read-only</span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {systemRoles.map(r => <RoleCard key={r._id} role={r} onEdit={openEdit} onDelete={setDeleteRole} />)}
            </div>
          </div>
        </>
      )}

      <RoleModal open={modalOpen} onClose={closeModal} existing={editRole} />
      <DeleteConfirm role={deleteRole} onClose={() => setDeleteRole(null)} />
    </div>
  );
}